import { Injectable, Injector } from '@angular/core';
import { BaseService } from 'src/app/common/base/base.service';
import { SCFMaster, SCFAmendment } from 'src/app/components/scf-v2/scf';
import { ScfV2Service, } from 'src/app/components/scf-v2/scf-v2.service';
import { WfSignatureResource } from 'src/app/components/workflow/workflow';
import { LogForm, LogUpdate } from '../../form-history-services/form-history.service';
import { IFormHistoryService } from '../../form-history-services/i-form-history-service';
import { FormHistoryServicesHelper } from '../../form-history-services/form-history-services-helper';

@Injectable({
  providedIn: 'root'
})
export class FormHistoryScfService extends BaseService<any, number> implements IFormHistoryService {

  amendmentSectionNumber = 6;
  helper = FormHistoryServicesHelper;

  constructor(
    protected override injector: Injector,
    private service: ScfV2Service
  ) {
    super(injector, '');
  }

  async processForm(form: any, logForm: LogForm, scf: SCFMaster) {

    if (scf) {
      // User names
      form.createdByName = this.users?.find(u => u.id === form.createdBy)?.name;
      form.requesterName = this.users?.find(u => u.id === form.requesterId)?.name;

      const shieldings = form.shieldingAffected ? JSON.parse(form.shieldingAffected) : [];
      form.shieldingNames = shieldings
        .map((s: any) => s?.childResourceName)
        .filter(Boolean)
        .join(', ');

      // Holdpoints (only originals)
      form.scfHoldPoints = scf?.scfHoldPoints?.filter(hp => !hp.amendedID);
      logForm.updates = this.processUpdates(logForm, scf);
      logForm.originalBody = form;
    }
    return logForm;
  }

  /**
   * Displays saved SCF amendment snapshots using the same history entries as their workflow signatures.
   * @param logForm History payload containing the original and amended snapshots.
   * @param scf Current SCF used to resolve hold points omitted from older snapshots.
   */
  processUpdates(logForm: LogForm, scf: SCFMaster) {
    // ---- Amendments list ----
    const amendmentsSet = new Set<number>(this.helper.getAmendmentList(logForm.signatures ?? []));

    // ---- Updates ----
    // Like RSF, retain returned history instead of hiding snapshots when live amendment matching fails.
    const updates = logForm?.updates ?? [];

    updates?.forEach(update => {
      // Parse old body
      if (update.prevBodyJson) {
        const prev = JSON.parse(update.prevBodyJson) as SCFMaster;

        if (prev) {
          // Shielding names
          const shieldings = prev.shieldingAffected
            ? JSON.parse(prev.shieldingAffected)
            : [];

          prev.shieldingNames = shieldings
            .map((s: any) => s?.childResourceName)
            .filter(Boolean)
            .join(', ');

          // User names
          const users = this.getUsers() ?? [];
          prev.createdByName = users.find(u => u.id === prev?.createdBy)?.name;
          prev.requesterName = users.find(u => u.id === prev?.requesterId)?.name;
        }

        update.prevBody = prev;

        if (update.amendmentId && amendmentsSet)
          update.header = this.helper.getLogHeadText(update.amendmentId, amendmentsSet);
      }
    });

    // Extra info
    generateAmendmentViews(scf, updates);

    return updates;

    // Helpers



    /**
     * Builds read-only amendment views from saved bodies rather than the latest live form.
     * @param scf Current SCF supplying legacy hold-point details.
     * @param updates Saved amendment entries to decorate in place.
     */
    function generateAmendmentViews(scf?: SCFMaster, updates?: LogUpdate[]) {
      if (!scf || updates?.length === 0) return;

      updates?.forEach(update => {
        if (!update.prevBody) return;

        const previous = update.prevBody as SCFMaster;
        const amendmentID = previous.scfAmendments?.find(a => a.active)?.id ?? getAmendmentID(update.amendmentId, scf);

        update.currentBody = update.prevBody as SCFMaster;
        const amended = update.currentBodyJson ? JSON.parse(update.currentBodyJson) as SCFMaster : previous;
        update.currentBody.scfHoldPoints = (amended.scfHoldPoints ?? scf.scfHoldPoints)?.filter(hp => amendmentID > 0 && (hp.amendedID == amendmentID || hp.amendmentID == amendmentID));

        // Keep completed amendments fixed to their saved content; pending snapshots may contain an active amendment.
        if (amended) {
          const pendingAmendment = amended.scfAmendments?.find(a => a.active);
          update.currentBody.scfAmendments = [pendingAmendment ? { ...pendingAmendment, active: true } : createAmendment(amended)];
        }
      });
    }

    /**
     * Resolves legacy hold-point ownership without renumbering or changing the live amendments.
     * @param wfAmendmentID Workflow amendment identifier from the history entry.
     * @param scf Current SCF containing physical amendment identifiers.
     */
    function getAmendmentID(wfAmendmentID?: number, scf?: SCFMaster) {
      const amendmentIndex = Array.from(amendmentsSet).indexOf(wfAmendmentID ?? 0);
      // Include canceled amendments so later hold points do not shift to an earlier amendment.
      return [...(scf?.scfAmendments ?? [])].sort((a, b) => a.id - b.id)[amendmentIndex]?.id ?? 0;
    }

    // function getAmendmentList(wfSignatures: WfSignatureResource[]): number[] {
    //   let olderAmendmentId = -1;
    //   let res: number[] = [];
    //   wfSignatures.map(t => {
    //     if (t.amendmentID !== olderAmendmentId) {
    //       olderAmendmentId = t.amendmentID;
    //       res.push(olderAmendmentId);
    //     }
    //   });

    //   res = [...new Set(res)].sort((a, b) => a - b);
    //   return res;
    // }

    function createAmendment(scfMaster: SCFMaster): SCFAmendment {
      return {
        id: 0,
        active: true,
        scfID: scfMaster?.id,
        name: scfMaster.name,
        description: scfMaster.description,
        status: scfMaster.status,
        type: scfMaster.type,
        createdOn: scfMaster.createdOn,
        createdBy: scfMaster.createdBy,
        locations: scfMaster.locations,
        locationNames: scfMaster.locationNames,
        locationIds: scfMaster.locationIds,
        accRFShielding: scfMaster.accRFShielding,
        blShielding: scfMaster.blShielding,
        permanentAccRemoval: scfMaster.permanentAccRemoval,
        permanentBLRemoval: scfMaster.permanentBLRemoval,
        tocaMovement: scfMaster.tocaMovement,
        shieldingAffected: scfMaster.shieldingAffected,
        requesterId: scfMaster.requesterId,
        estimatedEndOn: scfMaster.estimatedEndOn,
        estimatedStartOn: scfMaster.estimatedStartOn,
        notificationMessage: scfMaster.notificationMessage,
        notifyARC: scfMaster.notifyARC,
        notifyBLSci: scfMaster.notifyBLSci,
        notifyBRC: scfMaster.notifyBRC,
        notifyHP: scfMaster.notifyHP,
        notifyRF: scfMaster.notifyRF,
        notifyRP: scfMaster.notifyRP,
        notifySnA: scfMaster.notifySnA,
        scfRestrictionsV2: scfMaster.scfRestrictionsV2,
        relatedDocuments: scfMaster.relatedDocuments,
        relatedLinks: scfMaster.relatedLinks,

      } as SCFAmendment;
    }
  }

}
